/** Public plan slugs (admin catalog) ↔ legacy internal ids (Mongo subscriptions). */
export const PLAN_SLUGS = ['growing', 'scaling', 'enterprise'];

export const SLUG_TO_INTERNAL = {
  growing: 'startup',
  scaling: 'growth',
  enterprise: 'corporate',
};

export const INTERNAL_TO_SLUG = {
  startup: 'growing',
  growth: 'scaling',
  corporate: 'enterprise',
  free: null,
};

export const PLAN_TIER = { free: 0, startup: 1, growth: 2, corporate: 3 };

/** Short marketing names for CTAs (not admin-edited plan titles). */
export const PLAN_CTA_NAMES = {
  growing: 'Growing',
  scaling: 'Scaling',
  enterprise: 'Enterprise',
};

export function internalToSlug(internalId) {
  return INTERNAL_TO_SLUG[internalId] || null;
}

export function slugToInternal(slug) {
  return SLUG_TO_INTERNAL[slug] || slug;
}

/** Catalog add-on row from subscription.catalog_addons */
export function getCatalogAddonStatus(subscription, addonId) {
  const list = subscription?.catalog_addons || [];
  return list.find((a) => a.addon_id === addonId) || null;
}

/** Whether a catalog add-on is active (paid, plan-included, not expired). */
export function isCatalogAddonActive(subscription, addonId) {
  const row = getCatalogAddonStatus(subscription, addonId);
  if (row?.active || row?.included_with_plan) return true;
  const legacy = {
    ai_assistant: subscription?.ai_addon?.enabled,
    global_search: subscription?.search_addon?.enabled,
    advanced_search: subscription?.search_addon?.enabled,
    branding_control: subscription?.insights_addon?.enabled,
    full_branding_control: subscription?.insights_addon?.enabled,
    insights_analytics: Boolean(getCatalogAddonStatus(subscription, 'insights_analytics')?.active),
    faq_widget:
      (subscription?.faqs_addon?.purchased_widgets || 0) > 0 ||
      Boolean(subscription?.faqs_addon?.enabled),
    translation_localization:
      (subscription?.translation_addon?.purchased_languages || 0) > 0 ||
      Boolean(subscription?.translation_addon?.enabled),
    extra_team_members: (subscription?.purchased_extra_member_slots || 0) > 0,
    extra_custom_domains: (subscription?.purchased_extra_domain_slots || 0) > 0,
  };
  return Boolean(legacy[addonId]);
}

/** Purchased quantity for per-seat / per-domain add-ons. */
export function ownedAddonQuantity(subscription, addonId) {
  if (addonId === 'extra_team_members') {
    return Number(subscription?.purchased_extra_member_slots) || 0;
  }
  if (addonId === 'extra_custom_domains') {
    return Number(subscription?.purchased_extra_domain_slots) || 0;
  }
  if (addonId === 'faq_widget') {
    const row = getCatalogAddonStatus(subscription, 'faq_widget');
    if (row?.purchased_quantity != null) {
      return Number(row.purchased_quantity) || 0;
    }
    return Number(subscription?.faqs_addon?.purchased_widgets) || 0;
  }
  if (addonId === 'translation_localization') {
    const row = getCatalogAddonStatus(subscription, 'translation_localization');
    if (row?.purchased_quantity != null) {
      return Number(row.purchased_quantity) || 0;
    }
    return Number(subscription?.translation_addon?.purchased_languages) || 0;
  }
  return 0;
}

export function tieredAddonUnitCents(addon, billingCycle = 'monthly') {
  if (billingCycle === 'yearly') {
    return (
      Number(addon.extra_unit_yearly_price_cents) ||
      Number(addon.extra_unit_monthly_price_cents || 0) * 12
    );
  }
  return Number(addon.extra_unit_monthly_price_cents) || 0;
}

export function tieredAddonCheckoutCents(addon, quantity, owned, billingCycle = 'monthly') {
  const qty = Math.max(0, Number(quantity) || 0);
  const ownedCount = Math.max(0, Number(owned) || 0);
  if (qty <= 0) return 0;
  const baseCents =
    billingCycle === 'yearly'
      ? Number(addon.yearly_price_cents) || 0
      : Number(addon.monthly_price_cents) || 0;
  const extraCents = tieredAddonUnitCents(addon, billingCycle);
  let total = 0;
  for (let i = 0; i < qty; i += 1) {
    const slotIndex = ownedCount + i + 1;
    if (slotIndex === 1 && ownedCount === 0) {
      total += baseCents;
    } else {
      total += extraCents;
    }
  }
  return total;
}

export function addonLineAmountCents(addon, quantity, owned, billingCycle = 'monthly') {
  if (addon.is_tiered_quantity) {
    return tieredAddonCheckoutCents(addon, quantity, owned, billingCycle);
  }
  const unit =
    billingCycle === 'yearly' ? Number(addon.yearly_price_cents) : Number(addon.monthly_price_cents);
  return unit * Math.max(0, Number(quantity) || 0);
}

/** Lines to send to checkout (quantity = additional units for quantity-based add-ons). */
export function getCheckoutAddonLines(
  addons,
  selectedAddons,
  addonQuantities,
  subscription,
  billingCycle = 'monthly'
) {
  return (addons || [])
    .filter((a) => {
      const qty = Number(addonQuantities[a.id]) || 0;
      if (a.is_quantity_based) {
        return qty > 0;
      }
      return selectedAddons[a.id] && !isCatalogAddonActive(subscription, a.id);
    })
    .map((a) => {
      const qty = a.is_quantity_based ? Number(addonQuantities[a.id]) || 0 : 1;
      const owned = ownedAddonQuantity(subscription, a.id);
      const amountCents = addonLineAmountCents(a, qty, owned, billingCycle);
      const unitCents =
        billingCycle === 'yearly' ? a.yearly_price_cents : a.monthly_price_cents;
      return {
        addonId: a.id,
        name: a.name,
        quantity: qty,
        cents: unitCents,
        amountCents,
        isQuantityBased: a.is_quantity_based,
        isTieredQuantity: a.is_tiered_quantity,
      };
    });
}

export function centsToUsd(cents) {
  return (Number(cents) || 0) / 100;
}

export function formatUsdFromCents(cents, { decimals = 0 } = {}) {
  const n = centsToUsd(cents);
  return `$${n.toFixed(decimals)}`;
}

export function yearlyMonthlyCents(yearlyCents) {
  const yr = Number(yearlyCents) || 0;
  if (yr <= 0) return 0;
  return Math.round(yr / 12);
}

export function savingsPercent(monthlyCents, yearlyCents) {
  const annualAtMonthly = (Number(monthlyCents) || 0) * 12;
  const yr = Number(yearlyCents) || 0;
  if (annualAtMonthly <= 0 || yr <= 0 || yr >= annualAtMonthly) return 0;
  return Math.round((1 - yr / annualAtMonthly) * 100);
}

export function planCtaLabel({
  slug,
  planName,
  internalPlanId,
  isTrialing,
  tierCompare,
  renewalRequired = false,
}) {
  if (slug === 'enterprise') return 'Contact Sales';
  // A lapsed plan is not a "current" plan — the customer has to be able to buy it back.
  if (renewalRequired) {
    if (tierCompare === 0) return 'Renew plan';
    if (tierCompare < 0) return 'Switch & pay';
    return 'Upgrade & pay';
  }
  if (tierCompare === 0) return 'Current Plan';
  if (tierCompare < 0) return 'Downgrade';
  if (isTrialing && (slug === 'growing' || slug === 'scaling')) {
    return `Upgrade to ${PLAN_CTA_NAMES[slug] || planName || slug}`;
  }
  if (!internalPlanId || internalPlanId === 'free') return 'Select plan';
  return 'Upgrade';
}

/** Features shown on the public pricing card (admin-managed list). */
export function includedFeatures(plan) {
  return (plan?.features || [])
    .filter((f) => {
      const label = (f.label || '').trim();
      if (!label) return false;
      return f.included !== false;
    })
    .map((f) => ({
      ...f,
      label: (f.label || '').trim(),
      included: true,
    }));
}

export function addonsForPlan(addons, planSlug) {
  return (addons || []).filter(
    (a) => a.is_active !== false && (a.available_on_plans || []).includes(planSlug)
  );
}

export function showAddonsSection(internalPlanId) {
  if (!internalPlanId || internalPlanId === 'free') return true;
  return internalPlanId === 'startup' || internalPlanId === 'growth';
}

/** Normalize admin catalog, legacy /billing/plans, or mixed API responses. */
export function normalizePricingCatalog(raw) {
  if (!raw || typeof raw !== 'object') {
    return { plans: [], addons: [], free_trial_days: 14 };
  }

  const plans = (raw.plans || []).map((p) => {
    if (PLAN_SLUGS.includes(p.id)) {
      return p;
    }
    const slug = INTERNAL_TO_SLUG[p.id];
    if (!slug) {
      return p;
    }

    let features = [];
    if (Array.isArray(p.features)) {
      features = p.features;
    } else if (Array.isArray(p.highlight_features)) {
      features = p.highlight_features.map((label, idx) => ({
        id: `feature_${idx}`,
        label,
        included: true,
      }));
    }

    return {
      id: slug,
      internal_id: p.id,
      name: p.name,
      tagline: p.description || p.tagline || '',
      monthly_price_cents:
        p.monthly_price_cents != null
          ? p.monthly_price_cents
          : Math.round((Number(p.price_usd) || 0) * 100),
      yearly_price_cents:
        p.yearly_price_cents != null
          ? p.yearly_price_cents
          : Math.round((Number(p.price_usd_yearly) || 0) * 100),
      is_enterprise_contact:
        p.is_enterprise_contact ?? p.pricing_display === 'contact_sales',
      is_highlighted: p.is_highlighted ?? p.badge === 'Most Popular',
      features,
      included_addons: p.included_addons || [],
    };
  });

  return {
    plans: plans.filter((p) => PLAN_SLUGS.includes(p.id)),
    addons: raw.addons || [],
    free_trial_days: raw.free_trial_days ?? 14,
    payments_enabled: raw.payments_enabled,
    stripe_enabled: raw.stripe_enabled,
    contact_sales_email: raw.contact_sales_email,
  };
}
